import { useSyncExternalStore } from "react";
import { storage } from "@rekati/utils";

/*
 * The attendee's bearer token (docs/frontend-integration.md 3): in memory and
 * in localStorage so a reload keeps the attendee signed in. Never in a URL.
 * A 401 from any authenticated call clears it (see app/api.ts).
 */
export interface AttendeeSessionState {
  token: string;
  expiresAt: string;
}

const KEY = "rekati.attendee.session";
const store = storage("local");
const listeners = new Set<() => void>();

function load(): AttendeeSessionState | null {
  const s = store.read<AttendeeSessionState>(KEY);
  if (!s?.token || !s.expiresAt) return null;
  return new Date(s.expiresAt).getTime() > Date.now() ? s : null;
}

let current: AttendeeSessionState | null = load();

function emit() {
  for (const l of listeners) l();
}

export const attendeeSession = {
  get: () => current,
  token: () => current?.token ?? null,
  set(next: AttendeeSessionState) {
    current = next;
    store.write(KEY, next);
    emit();
  },
  clear() {
    if (!current) return;
    current = null;
    store.remove(KEY);
    emit();
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useAttendeeSession(): AttendeeSessionState | null {
  return useSyncExternalStore(attendeeSession.subscribe, attendeeSession.get, attendeeSession.get);
}
